import { Link } from "react-router-dom";
import type { Session } from "../lib/api";
import { timeAgo, tokenSummary } from "../lib/format";
import { MonoChip, StatusDot, StatusPill } from "../kit";

/** Shared session rows — used by Activity and the machine detail page.
 *  Status language rides the kit registry (phase 7 Track A); empty states
 *  are the caller's job (they differ per page). */
export function SessionList({
  sessions,
  machineName,
}: {
  sessions: Session[];
  machineName: (machineId: string) => string;
}) {
  return (
    <div>
      {sessions.map((s) => {
        const tokens = tokenSummary(s);
        const live = s.status === "running" || s.status === "starting";
        // needs-human owns the dot's signal (2026-06-11): amber + pulse so a
        // waiting session reads differently from one actively working.
        const needs = !!s.needsHuman && s.status !== "ended";
        // Map onto the kit STATUS registry tones; keep the real status text.
        const pillStatus =
          s.status === "error"
            ? "failed"
            : s.status === "starting"
              ? "running"
              : s.status;
        return (
          <Link
            key={s.sessionId}
            to={`/sessions/${s.sessionId}`}
            className="dsh-session-row"
          >
            <StatusDot
              tone={
                needs
                  ? "warn"
                  : live
                    ? "live"
                    : s.status === "error"
                      ? "danger"
                      : "off"
              }
              pulse={needs || live}
              size={7}
            />
            <span className="agent">{s.agent}</span>
            <span className="meta">{machineName(s.machineId)}</span>
            <StatusPill status={pillStatus}>{s.status}</StatusPill>
            {/* phase 12 §T9: the runtime this session ran on. Shown only when
                NOT plain Claude (default) — a codex session reads distinctly;
                Claude-only tenants see no extra chip. */}
            {s.runtime && s.runtime !== "claude" && (
              <span title={`runtime · ${s.runtime}`}>
                <MonoChip tone="accent">{s.runtime}</MonoChip>
              </span>
            )}
            {/* phase 9 §C/§D: assignment chips where the dispatch set them */}
            {s.model && (
              <span title="model the session was dispatched with (phase 9)">
                <MonoChip tone="ghost">{s.model}</MonoChip>
              </span>
            )}
            {s.effort && (
              <span title="effort the session was dispatched with (phase 9)">
                <MonoChip tone="ghost">effort {s.effort}</MonoChip>
              </span>
            )}
            {/* dispatch profiles: intensity rung where a preamble was injected */}
            {s.intensity && s.intensity !== "standard" && (
              <span title="intensity the session was dispatched with (dispatch profiles)">
                <MonoChip tone="ghost">{s.intensity}</MonoChip>
              </span>
            )}
            {/* session-autoClose: ends when the task finishes (fresh next run) */}
            {s.autoClose && (
              <span title="closes when the task finishes — starts fresh next run">
                <MonoChip tone="ghost">auto-close</MonoChip>
              </span>
            )}
            {/* phase 9 §B watchdog: alert only — never kills the session */}
            {s.overBudget && (
              <span title="crossed the per-session token cap — alert only, the session keeps running">
                <StatusPill status="over-budget" />
              </span>
            )}
            {s.needsHuman && <span className="dsh-needs-you">needs you</span>}
            {s.status === "error" && s.errorMessage && (
              <span className="err" title={s.errorMessage}>
                {s.errorMessage}
              </span>
            )}
            <span className="right">
              {tokens && <span className="meta">{tokens}</span>}
              <span className="meta">{timeAgo(s.startedAt)}</span>
            </span>
          </Link>
        );
      })}
    </div>
  );
}
